import { Box, Button, ButtonProps, SxProps, Theme } from "@mui/material";
import {
  Controller,
  FieldError,
  FieldValues,
  UseControllerProps,
} from "react-hook-form";
import { ISchemaUpload, UploadService } from "../../services/Upload";

import { useState } from "react";
import { toast } from "react-hot-toast";

interface Props<T extends FieldValues> extends UseControllerProps<T> {
  schema: ISchemaUpload;
  label?: string;
  error?: FieldError | undefined;
  buttonColor?: ButtonProps["color"];
  buttonSx?: SxProps<Theme>;
  accept?: string;
  multiple?: boolean;
  maxFiles?: number;
  maxFileSizeMB?: number;
}

const UploadFileField = <T extends FieldValues>({
  schema,
  label,
  error,
  name,
  control,
  rules,
  buttonColor = "primary",
  buttonSx,
  accept = "image/*",
  multiple = false,
  maxFiles,
  maxFileSizeMB,
}: Props<T>) => {
  const [loading, setLoading] = useState(false);

  return (
    <Box
      width={"100%"}
      display="flex"
      flexDirection={"column"}
      gap={1}
      border="1px solid #6f6c6c"
      padding={1}
      borderRadius={"5px"}
    >
      <Controller
        name={name}
        control={control}
        rules={rules}
        render={({ field }) => {
          const { value, onChange, onBlur } = field;
          return (
            <>
              <Button
                variant="contained"
                component="label"
                fullWidth
                color={buttonColor}
                sx={buttonSx}
              >
                {label ?? "Select File"}
                <input
                  type="file"
                  multiple={multiple}
                  name="file"
                  onBlur={onBlur}
                  accept={accept}
                  hidden
                  onChange={async (e) => {
                    const fileList = e.target.files;
                    if (!fileList || fileList.length === 0) return;

                    const files = Array.from(fileList);

                    // Validate: max file size per image
                    if (maxFileSizeMB) {
                      const maxBytes = maxFileSizeMB * 1024 * 1024;
                      if (files.some((f) => f.size > maxBytes)) {
                        toast.error(`The file must not exceed ${maxFileSizeMB}MB`);
                        e.target.value = "";
                        return;
                      }
                    }

                    // Validate: max number of files
                    const currentCount = Array.isArray(value) ? value.length : value ? 1 : 0;
                    if (maxFiles && currentCount + files.length > maxFiles) {
                      toast.error(`You can upload up to ${maxFiles} file(s) only`);
                      e.target.value = "";
                      return;
                    }

                    try {
                      setLoading(true);
                      const uploadedLinks: string[] = [];
                      for (let i = 0; i < files.length; i++) {
                        const res = await UploadService.upload({
                          file: files[i],
                          schema: schema,
                        });
                        if (res?.ok) uploadedLinks.push(res.data.link);
                      }
                      if (multiple) {
                        const currentVal = Array.isArray(value) ? value : value ? [value] : [];
                        onChange([...currentVal, ...uploadedLinks]);
                      } else if (uploadedLinks.length > 0) {
                        onChange(uploadedLinks[0]);
                      }
                    } catch (error) {
                      console.log("error", error);
                    } finally {
                      setLoading(false);
                      e.target.value = "";
                    }
                  }}
                />
              </Button>

              {loading ? (
                <Box textAlign={"center"}>Loading</Box>
              ) : (
                (multiple ? Array.isArray(value) && value.length > 0 : value) && (
                  <Box display="flex" gap={1} flexWrap="wrap" justifyContent="center">
                    {multiple
                      ? (value as any[]).map((v: string, idx: number) => (
                          <Box key={idx} position="relative" textAlign="center">
                            <img src={v} alt={`uploaded-preview-${idx}`} style={{ width: 120, height: 80, objectFit: 'cover', borderRadius: 4 }} />
                            <Box
                              component="button"
                              onClick={() => {
                                const arr = Array.isArray(value) ? [...value] : [];
                                arr.splice(idx, 1);
                                onChange(arr);
                              }}
                              sx={{
                                position: 'absolute',
                                top: -8,
                                right: -8,
                                bgcolor: 'rgba(0,0,0,0.6)',
                                color: 'white',
                                border: 'none',
                                borderRadius: '50%',
                                width: 22,
                                height: 22,
                                cursor: 'pointer',
                              }}
                            >
                              ×
                            </Box>
                          </Box>
                        ))
                      : (
                        <img
                          src={String(value)}
                          alt="uploaded-preview"
                          style={{ width: "50%" }}
                        />
                      )}
                  </Box>
                )
              )}
              {error && (
                <span
                  style={{
                    color: "red",
                    fontSize: "11px",
                    marginTop: "-10px",
                  }}
                >
                  {error?.message}
                </span>
              )}
            </>
          );
        }}
      />
    </Box>
  );
};

export default UploadFileField;
